import { useState, useCallback } from 'react';
import { HumanVerificationService } from '../services/humanVerificationService';
import {
  IUseHumanVerificationOptions,
  IUseHumanVerificationReturn,
  IVerifyHumanResponse
} from '../types/humanVerification';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../services/application-insights/appInsightsConstants';

export const useHumanVerification = (
  options: IUseHumanVerificationOptions
): IUseHumanVerificationReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verify = useCallback(async (token: string): Promise<IVerifyHumanResponse> => {
    const verificationId = `human-verification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setIsLoading(true);
    setError(null);

    trackEvent(
      AppInsightsActions.HUMAN_VERIFICATION_ATTEMPT,
      {
        verificationId,
        component: AppInsightsComponents.HUMAN_VERIFICATION_HOOK,
        provider: options.provider,
        action: options.action || 'unknown'
      }
    );

    try {
      let result: IVerifyHumanResponse;

      if (options.provider === 'turnstile') {
        result = await HumanVerificationService.verifyTurnstile(
          token,
          options.action
        );
      } else {
        result = await HumanVerificationService.verifyRecaptcha(
          token,
          options.action,
          options.minScore
        );
      }

      if (result.isSuccess) {
        trackEvent(
          AppInsightsActions.HUMAN_VERIFICATION_SUCCESS,
          {
            verificationId,
            component: AppInsightsComponents.HUMAN_VERIFICATION_HOOK,
            provider: options.provider,
            action: options.action || 'unknown',
            score: result.score?.toString() || 'unknown'
          }
        );

        if (options.onVerified) {
          options.onVerified(token, result);
        }
      } else {
        trackEvent(
          AppInsightsActions.HUMAN_VERIFICATION_FAILURE,
          {
            verificationId,
            component: AppInsightsComponents.HUMAN_VERIFICATION_HOOK,
            provider: options.provider,
            action: options.action || 'unknown',
            errorMessage: result.errorMessage || 'Verification failed',
            reason: 'verification-rejected'
          }
        );

        if (options.onError) {
          options.onError(result.errorMessage || 'Verification failed');
        }
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMessage);

      trackExceptionWithContext(
        err instanceof Error ? err : new Error(String(err)),
        {
          component: AppInsightsComponents.HUMAN_VERIFICATION_HOOK,
          action: AppInsightsActions.HUMAN_VERIFICATION_ATTEMPT,
          additionalData: {
            verificationId,
            errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_ERROR,
            provider: options.provider,
            action: options.action || 'unknown'
          }
        }
      );

      if (options.onError) {
        options.onError(errorMessage);
      }

      // Return a failed response for consistency
      return {
        isSuccess: false,
        status: 'Failed',
        errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    verify,
    isLoading,
    error,
    reset,
  };
};